import {
  MessageSquare,
  Users,
  DollarSign,
  TrendingUp,
  ChevronRight,
  AlertCircle,
  Zap,
  Calendar,
  Bell,
} from 'lucide-react';
import React, { useState, useEffect } from 'react';
import MessageCreationTool from './MessageCreationTool';

const MessageCenter = () => {
  const [activeTab, setActiveTab] = useState('opportunities');
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [isMobile, setIsMobile] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    // Check on mount
    checkMobile();

    // Add event listener
    window.addEventListener('resize', checkMobile);

    // Cleanup
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Mock data for demonstration
  const messagingOpportunities = [
    {
      id: 1,
      type: 'reminder',
      title: "It's Thursday - Send Weekend Reminder",
      subtitle: "347 ticket holders for Saturday's races",
      icon: <Calendar className="w-5 h-5" />,
      audience: 347,
      smsAudience: 347,
      pushAudience: 243, // ~70% have the app
      estimatedRevenue: '$460',
      timing: 'optimal',
      event: 'Saturday Night Racing',
      recommendedChannel: 'sms',
      messages: {
        // eslint-disable-next-line jsx-a11y/accessible-emoji
        sms: "🏁 Don't forget about Saturday Night Racing! Gates open at 5PM. Get your tickets now: https://tkt.link/saturday-racing",
        // eslint-disable-next-line jsx-a11y/accessible-emoji
        push: "🏁 Saturday Night Racing starts soon! Gates open at 5PM. Don't miss the action!",
      },
    },
    {
      id: 2,
      type: 'low-attendance',
      title: 'Low Attendance Alert',
      subtitle: 'Friday event at 62% capacity - boost sales',
      icon: <AlertCircle className="w-5 h-5" />,
      audience: 892,
      smsAudience: 892,
      pushAudience: 624, // ~70% have the app
      estimatedRevenue: '$1,240',
      timing: 'urgent',
      event: 'Friday Thunder Series',
      recommendedChannel: 'sms',
      messages: {
        // eslint-disable-next-line jsx-a11y/accessible-emoji
        sms: '⚡ Friday Thunder Series still has seats available! Save 25% with code LASTCHANCE. Book now: https://tkt.link/friday-thunder',
        // eslint-disable-next-line jsx-a11y/accessible-emoji
        push: '⚡ Last chance for Friday Thunder Series! Save 25% with code LASTCHANCE. Limited seats remaining!',
      },
    },
    {
      id: 3,
      type: 're-engagement',
      title: 'Re-engage Past Attendees',
      subtitle: "156 customers haven't attended in 30+ days",
      icon: <Users className="w-5 h-5" />,
      audience: 156,
      smsAudience: 156,
      pushAudience: 109, // ~70% have the app
      estimatedRevenue: '$780',
      timing: 'suggested',
      event: 'General Promotion',
      recommendedChannel: 'push',
      messages: {
        // eslint-disable-next-line jsx-a11y/accessible-emoji
        sms: '🏁 We miss you at the track! Come back with 30% off your next visit. Use code WELCOME30: https://tkt.link/comeback',
        // eslint-disable-next-line jsx-a11y/accessible-emoji
        push: '🏁 We miss you at the track! Come back with 30% off your next visit using code WELCOME30.',
      },
    },
    {
      id: 4,
      type: 'credit-reminder',
      title: 'Credit Balance Reminder',
      subtitle: '127 customers have $3,420 in unused credits',
      icon: <DollarSign className="w-5 h-5" />,
      audience: 127,
      smsAudience: 127,
      pushAudience: 89, // ~70% have the app
      estimatedRevenue: '$2,850',
      timing: 'optimal',
      event: 'Credit Redemption',
      recommendedChannel: 'push',
      messages: {
        // eslint-disable-next-line jsx-a11y/accessible-emoji
        sms: '💰 You have unused credits waiting! Use them for upcoming races before they expire: https://tkt.link/use-credits',
        // eslint-disable-next-line jsx-a11y/accessible-emoji
        push: "💰 Don't let your credits expire! Use them for upcoming races and save on your next visit.",
      },
    },
  ];

  const recentMessages = [
    {
      id: 1,
      date: 'Jul 22, 2025',
      title: 'Weekend Race Reminder',
      message:
        "Don't forget - gates open at 5PM for tonight's Thunder Series racing! Limited parking available in Lot A. Concessions open early with new menu items.",
      sentBy: 'John Smith',
      audience: 423,
      sent: 423,
      delivered: 421,
      deliveryRate: '99.5%',
      clicked: 87,
      clickRate: '20.6%',
      cost: '$8.46',
      revenue: '$1,260',
      roi: '14.9x',
      status: 'complete',
      channel: 'sms',
    },
    {
      id: 2,
      date: 'Jul 20, 2025',
      title: 'Rain Delay Update',
      message:
        "Due to weather conditions, tonight's races will be delayed by 30 minutes. Gates still open at regular time. Hot chocolate available at concessions!",
      sentBy: 'Sarah Johnson',
      audience: 289,
      sent: 287,
      delivered: 285,
      deliveryRate: '99.3%',
      clicked: 45,
      clickRate: '15.7%',
      cost: '$5.74',
      revenue: '$340',
      roi: '5.9x',
      status: 'complete',
      channel: 'sms',
    },
    {
      id: 3,
      date: 'Jul 18, 2025',
      title: 'Early Bird Special',
      message:
        "Save 20% on all tickets for next weekend's Summer Championship Series! Use code EARLY20 at checkout. This weekend only - don't miss out on the biggest race of the season.",
      sentBy: 'Mike Davis',
      audience: 1045,
      sent: 1042,
      delivered: 1038,
      deliveryRate: '99.6%',
      clicked: 234,
      clickRate: '22.5%',
      cost: 'Free',
      revenue: '$2,380',
      roi: '∞',
      status: 'complete',
      channel: 'push',
    },
  ];

  const handleQuickSend = (opportunity) => {
    setSelectedOpportunity(opportunity);
    setShowMessageModal(true);
  };

  const handleSendComplete = () => {
    setShowMessageModal(false);
    setSelectedOpportunity(null);
    // Here you could refresh the message history or show a success notification
  };

  const toggleRowExpansion = (messageId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(messageId)) {
      newExpanded.delete(messageId);
    } else {
      newExpanded.add(messageId);
    }
    setExpandedRows(newExpanded);
  };

  return (
    <div
      style={{
        backgroundColor: '#F5F7FA',
        minHeight: '100vh',
        padding: '24px',
      }}
    >
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '32px' }}>
          <h1
            style={{
              fontSize: '28px',
              fontWeight: 600,
              color: '#1F2937',
              marginBottom: '8px',
            }}
          >
            Message Center
          </h1>
          <p style={{ fontSize: '14px', color: '#6B7280' }}>
            Send targeted messages to drive ticket sales and engage your
            audience
          </p>
        </div>

        {/* Revenue Summary Card */}
        <div
          style={{
            backgroundColor: '#FFFFFF',
            borderRadius: '8px',
            padding: '20px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            marginBottom: '24px',
            border: '1px solid #E5E7EB',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '16px',
              flexWrap: 'wrap',
              gap: '12px',
            }}
          >
            <h2
              style={{
                fontSize: '18px',
                fontWeight: 600,
                color: '#1F2937',
                margin: 0,
              }}
            >
              This Month's Impact
            </h2>
            <span
              style={{
                backgroundColor: '#D1FAE5',
                color: '#10B981',
                padding: '4px 8px',
                borderRadius: '4px',
                fontSize: '12px',
                fontWeight: 600,
                textTransform: 'uppercase',
              }}
            >
              +34% vs last month
            </span>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '20px',
            }}
          >
            <div>
              <p
                style={{
                  fontSize: '12px',
                  color: '#6B7280',
                  marginBottom: '4px',
                  textTransform: 'uppercase',
                  fontWeight: 600,
                }}
              >
                Messages Sent
              </p>
              <p
                style={{ fontSize: '36px', fontWeight: 600, color: '#1F2937' }}
              >
                12
              </p>
              <p
                style={{ fontSize: '12px', color: '#10B981', marginTop: '8px' }}
              >
                ↑ 4 from last month
              </p>
            </div>
            <div>
              <p
                style={{
                  fontSize: '12px',
                  color: '#6B7280',
                  marginBottom: '4px',
                  textTransform: 'uppercase',
                  fontWeight: 600,
                }}
              >
                Total Revenue
              </p>
              <p
                style={{ fontSize: '36px', fontWeight: 600, color: '#1F2937' }}
              >
                $8,420
              </p>
              <p
                style={{ fontSize: '12px', color: '#10B981', marginTop: '8px' }}
              >
                ↑ $2,340 from last month
              </p>
            </div>
            <div>
              <p
                style={{
                  fontSize: '12px',
                  color: '#6B7280',
                  marginBottom: '4px',
                  textTransform: 'uppercase',
                  fontWeight: 600,
                }}
              >
                Average ROI
              </p>
              <p
                style={{ fontSize: '36px', fontWeight: 600, color: '#1F2937' }}
              >
                12.3x
              </p>
              <p
                style={{ fontSize: '12px', color: '#10B981', marginTop: '8px' }}
              >
                ↑ 2.1x improvement
              </p>
            </div>
            <div>
              <p
                style={{
                  fontSize: '12px',
                  color: '#6B7280',
                  marginBottom: '4px',
                  textTransform: 'uppercase',
                  fontWeight: 600,
                }}
              >
                Cost Per Sale
              </p>
              <p
                style={{ fontSize: '36px', fontWeight: 600, color: '#1F2937' }}
              >
                $0.84
              </p>
              <p
                style={{ fontSize: '12px', color: '#10B981', marginTop: '8px' }}
              >
                ↓ $0.23 from last month
              </p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div
          style={{
            display: 'flex',
            gap: '24px',
            marginBottom: '24px',
            borderBottom: '1px solid #E5E7EB',
          }}
        >
          <button
            onClick={() => setActiveTab('opportunities')}
            style={{
              padding: '12px 0',
              backgroundColor: 'transparent',
              border: 'none',
              fontSize: '14px',
              fontWeight: 600,
              color: activeTab === 'opportunities' ? '#FA4616' : '#6B7280',
              borderBottom:
                activeTab === 'opportunities' ? '2px solid #FA4616' : 'none',
              cursor: 'pointer',
              marginBottom: '-1px',
            }}
          >
            Messaging Opportunities
          </button>
          <button
            onClick={() => setActiveTab('history')}
            style={{
              padding: '12px 0',
              backgroundColor: 'transparent',
              border: 'none',
              fontSize: '14px',
              fontWeight: 600,
              color: activeTab === 'history' ? '#FA4616' : '#6B7280',
              borderBottom:
                activeTab === 'history' ? '2px solid #FA4616' : 'none',
              cursor: 'pointer',
              marginBottom: '-1px',
            }}
          >
            Message History
          </button>
        </div>

        {/* Content based on active tab */}
        {activeTab === 'opportunities' && (
          <div style={{ display: 'grid', gap: '16px' }}>
            {messagingOpportunities.map((opportunity) => (
              <div
                key={opportunity.id}
                style={{
                  backgroundColor: '#FFFFFF',
                  borderRadius: '8px',
                  padding: isMobile ? '16px' : '20px',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                  border: '1px solid #E5E7EB',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  ':hover': {
                    backgroundColor: '#F9FAFB',
                  },
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = '#F9FAFB')
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor = '#FFFFFF')
                }
              >
                {!isMobile ? (
                  // Desktop layout
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '16px',
                      }}
                    >
                      <div
                        style={{
                          backgroundColor:
                            opportunity.timing === 'optimal'
                              ? '#DBEAFE'
                              : opportunity.timing === 'urgent'
                              ? '#FEE2E2'
                              : '#F3F4F6',
                          padding: '12px',
                          borderRadius: '8px',
                        }}
                      >
                        {React.cloneElement(opportunity.icon, {
                          style: {
                            color:
                              opportunity.timing === 'optimal'
                                ? '#3B82F6'
                                : opportunity.timing === 'urgent'
                                ? '#EF4444'
                                : '#6B7280',
                          },
                        })}
                      </div>
                      <div>
                        <h3
                          style={{
                            fontSize: '16px',
                            fontWeight: 600,
                            color: '#1F2937',
                            marginBottom: '4px',
                          }}
                        >
                          {opportunity.title}
                        </h3>
                        <p
                          style={{
                            fontSize: '14px',
                            color: '#6B7280',
                            marginTop: '10px',
                          }}
                        >
                          {opportunity.subtitle}
                        </p>
                        <div
                          style={{
                            display: 'flex',
                            gap: '16px',
                            marginTop: '8px',
                          }}
                        >
                          <span
                            style={{
                              fontSize: '12px',
                              color: '#6B7280',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                            }}
                          >
                            <Users className="w-4 h-4" />
                            {opportunity.audience} recipients
                          </span>
                          <span
                            style={{
                              fontSize: '12px',
                              color: '#10B981',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              fontWeight: 600,
                            }}
                          >
                            <TrendingUp className="w-4 h-4" />
                            Est. {opportunity.estimatedRevenue} revenue
                          </span>
                          <span
                            style={{
                              fontSize: '12px',
                              color: '#6B7280',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                            }}
                          >
                            {opportunity.recommendedChannel === 'sms' ? (
                              <MessageSquare className="w-4 h-4" />
                            ) : (
                              <Bell className="w-4 h-4" />
                            )}
                            {opportunity.recommendedChannel === 'sms'
                              ? 'SMS'
                              : 'Push'}
                          </span>
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleQuickSend(opportunity);
                      }}
                      style={{
                        backgroundColor: '#FA4616',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '12px 20px',
                        fontSize: '14px',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        transition: 'background-color 0.2s',
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.backgroundColor = '#DC5A3D')
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.backgroundColor = '#FA4616')
                      }
                    >
                      <Zap className="w-4 h-4" />
                      Quick Send
                    </button>
                  </div>
                ) : (
                  // Mobile layout
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px',
                    }}
                  >
                    <div>
                      <h3
                        style={{
                          fontSize: '16px',
                          fontWeight: 600,
                          color: '#1F2937',
                          marginBottom: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                        }}
                      >
                        {React.cloneElement(opportunity.icon, {
                          className: 'w-4 h-4',
                          style: {
                            color:
                              opportunity.timing === 'optimal'
                                ? '#3B82F6'
                                : opportunity.timing === 'urgent'
                                ? '#EF4444'
                                : '#6B7280',
                          },
                        })}
                        {opportunity.title}
                      </h3>
                      <p style={{ fontSize: '14px', color: '#6B7280' }}>
                        {opportunity.subtitle}
                      </p>
                    </div>

                    {/* Metrics - we'll update this in the next step */}
                    <div
                      style={{ display: 'flex', gap: '16px', marginTop: '8px' }}
                    >
                      <span
                        style={{
                          fontSize: '12px',
                          color: '#6B7280',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                        }}
                      >
                        <Users className="w-4 h-4" />
                        {opportunity.audience} recipients
                      </span>
                      <span
                        style={{
                          fontSize: '12px',
                          color: '#10B981',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          fontWeight: 600,
                        }}
                      >
                        <TrendingUp className="w-4 h-4" />
                        Est. {opportunity.estimatedRevenue} revenue
                      </span>
                      <span
                        style={{
                          fontSize: '12px',
                          color: '#6B7280',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                        }}
                      >
                        {opportunity.recommendedChannel === 'sms' ? (
                          <MessageSquare className="w-4 h-4" />
                        ) : (
                          <Bell className="w-4 h-4" />
                        )}
                        {opportunity.recommendedChannel === 'sms'
                          ? 'SMS'
                          : 'Push'}
                      </span>
                    </div>

                    {/* Quick Send button - we'll update this in step 5 */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleQuickSend(opportunity);
                      }}
                      style={{
                        backgroundColor: '#FA4616',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '12px 20px',
                        fontSize: '14px',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        transition: 'background-color 0.2s',
                        width: '100%',
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.backgroundColor = '#DC5A3D')
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.backgroundColor = '#FA4616')
                      }
                    >
                      <Zap className="w-4 h-4" />
                      Quick Send
                    </button>
                  </div>
                )}
              </div>
            ))}

            {/* Create Custom Message Card */}
            <div
              style={{
                backgroundColor: '#FFFFFF',
                borderRadius: '8px',
                padding: '20px',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                border: '2px dashed #E5E7EB',
                cursor: 'pointer',
                transition: 'all 0.2s',
                textAlign: 'center',
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.borderColor = '#FA4616')
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.borderColor = '#E5E7EB')
              }
            >
              <MessageSquare
                className="w-8 h-8 mx-auto mb-2"
                style={{ color: '#6B7280' }}
              />
              <h3
                style={{
                  fontSize: '16px',
                  fontWeight: 600,
                  color: '#1F2937',
                  marginBottom: '4px',
                }}
              >
                Create Custom Message
              </h3>
              <p style={{ fontSize: '14px', color: '#6B7280' }}>
                Build your own message with advanced targeting options
              </p>
            </div>
          </div>
        )}

        {activeTab === 'history' && (
          <div>
            {/* Desktop Table View */}
            <div
              style={{
                display: window.innerWidth > 768 ? 'block' : 'none',
                backgroundColor: '#FFFFFF',
                borderRadius: '8px',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                border: '1px solid #E5E7EB',
                overflow: 'hidden',
              }}
            >
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#F9FAFB' }}>
                    <th
                      style={{
                        padding: '12px 24px',
                        textAlign: 'left',
                        fontSize: '12px',
                        fontWeight: 600,
                        color: '#6B7280',
                        textTransform: 'uppercase',
                      }}
                    >
                      Date
                    </th>
                    <th
                      style={{
                        padding: '12px 24px',
                        textAlign: 'left',
                        fontSize: '12px',
                        fontWeight: 600,
                        color: '#6B7280',
                        textTransform: 'uppercase',
                      }}
                    >
                      Message
                    </th>
                    <th
                      style={{
                        padding: '12px 24px',
                        textAlign: 'left',
                        fontSize: '12px',
                        fontWeight: 600,
                        color: '#6B7280',
                        textTransform: 'uppercase',
                      }}
                    >
                      Sent By
                    </th>
                    <th
                      style={{
                        padding: '12px 24px',
                        textAlign: 'left',
                        fontSize: '12px',
                        fontWeight: 600,
                        color: '#6B7280',
                        textTransform: 'uppercase',
                      }}
                    >
                      Channel
                    </th>
                    <th
                      style={{
                        padding: '12px 24px',
                        textAlign: 'left',
                        fontSize: '12px',
                        fontWeight: 600,
                        color: '#6B7280',
                        textTransform: 'uppercase',
                      }}
                    >
                      Revenue
                    </th>
                    <th
                      style={{
                        padding: '12px 24px',
                        textAlign: 'left',
                        fontSize: '12px',
                        fontWeight: 600,
                        color: '#6B7280',
                        textTransform: 'uppercase',
                      }}
                    >
                      ROI
                    </th>
                    <th style={{ width: '48px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {recentMessages.map((message) => (
                    <React.Fragment key={message.id}>
                      <tr
                        style={{
                          borderTop: '1px solid #F3F4F6',
                          cursor: 'pointer',
                          backgroundColor: expandedRows.has(message.id)
                            ? '#F9FAFB'
                            : '#FFFFFF',
                        }}
                        onClick={() => toggleRowExpansion(message.id)}
                      >
                        <td
                          style={{
                            padding: '16px 24px',
                            fontSize: '14px',
                            color: '#6B7280',
                          }}
                        >
                          {message.date}
                        </td>
                        <td
                          style={{
                            padding: '16px 24px',
                            fontSize: '14px',
                            color: '#1F2937',
                            fontWeight: 500,
                          }}
                        >
                          {message.title}
                        </td>
                        <td
                          style={{
                            padding: '16px 24px',
                            fontSize: '14px',
                            color: '#6B7280',
                          }}
                        >
                          {message.sentBy}
                        </td>
                        <td
                          style={{
                            padding: '16px 24px',
                            fontSize: '14px',
                            color: '#6B7280',
                          }}
                        >
                          <span
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                            }}
                          >
                            {message.channel === 'sms' ? (
                              <MessageSquare className="w-4 h-4" />
                            ) : (
                              <Bell className="w-4 h-4" />
                            )}
                            {message.channel === 'sms' ? 'SMS' : 'Push'}
                          </span>
                        </td>
                        <td
                          style={{
                            padding: '16px 24px',
                            fontSize: '14px',
                            color: '#10B981',
                            fontWeight: 600,
                          }}
                        >
                          {message.revenue}
                        </td>
                        <td style={{ padding: '16px 24px' }}>
                          <span
                            style={{
                              backgroundColor: '#D1FAE5',
                              color: '#10B981',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              fontWeight: 600,
                            }}
                          >
                            {message.roi}
                          </span>
                        </td>
                        <td style={{ padding: '16px 24px' }}>
                          <ChevronRight
                            className={`w-5 h-5 transition-transform ${
                              expandedRows.has(message.id) ? 'rotate-90' : ''
                            }`}
                            style={{ color: '#6B7280' }}
                          />
                        </td>
                      </tr>
                      {expandedRows.has(message.id) && (
                        <tr>
                          <td
                            colSpan="7"
                            style={{
                              padding: '0',
                              backgroundColor: '#F9FAFB',
                              borderTop: '1px solid #E5E7EB',
                            }}
                          >
                            <div style={{ padding: '24px' }}>
                              <div style={{ marginBottom: '16px' }}>
                                <h4
                                  style={{
                                    fontSize: '12px',
                                    fontWeight: 600,
                                    color: '#6B7280',
                                    textTransform: 'uppercase',
                                    marginBottom: '8px',
                                  }}
                                >
                                  Message
                                </h4>
                                <p
                                  style={{
                                    fontSize: '14px',
                                    color: '#333333',
                                    lineHeight: '1.6',
                                  }}
                                >
                                  {message.message}
                                </p>
                              </div>

                              <div
                                style={{
                                  display: 'grid',
                                  gridTemplateColumns: 'repeat(4, 1fr)',
                                  gap: '24px',
                                }}
                              >
                                <div>
                                  <p
                                    style={{
                                      fontSize: '12px',
                                      color: '#6B7280',
                                      marginBottom: '4px',
                                      fontWeight: 600,
                                    }}
                                  >
                                    SENT
                                  </p>
                                  <p
                                    style={{
                                      fontSize: '16px',
                                      color: '#1F2937',
                                      fontWeight: 600,
                                    }}
                                  >
                                    {message.sent.toLocaleString()}
                                  </p>
                                </div>
                                <div>
                                  <p
                                    style={{
                                      fontSize: '12px',
                                      color: '#6B7280',
                                      marginBottom: '4px',
                                      fontWeight: 600,
                                    }}
                                  >
                                    DELIVERED
                                  </p>
                                  <p
                                    style={{
                                      fontSize: '16px',
                                      color: '#1F2937',
                                      fontWeight: 600,
                                    }}
                                  >
                                    {message.delivered.toLocaleString()} (
                                    {message.deliveryRate})
                                  </p>
                                </div>
                                <div>
                                  <p
                                    style={{
                                      fontSize: '12px',
                                      color: '#6B7280',
                                      marginBottom: '4px',
                                      fontWeight: 600,
                                    }}
                                  >
                                    CLICKED
                                  </p>
                                  <p
                                    style={{
                                      fontSize: '16px',
                                      color: '#1F2937',
                                      fontWeight: 600,
                                    }}
                                  >
                                    {message.clicked} ({message.clickRate})
                                  </p>
                                </div>
                                <div>
                                  <p
                                    style={{
                                      fontSize: '12px',
                                      color: '#6B7280',
                                      marginBottom: '4px',
                                      fontWeight: 600,
                                    }}
                                  >
                                    COST
                                  </p>
                                  <p
                                    style={{
                                      fontSize: '16px',
                                      color: '#1F2937',
                                      fontWeight: 600,
                                    }}
                                  >
                                    {message.cost}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div
              style={{
                display: window.innerWidth <= 768 ? 'block' : 'none',
              }}
            >
              {recentMessages.map((message) => (
                <div
                  key={message.id}
                  style={{
                    backgroundColor: '#FFFFFF',
                    borderRadius: '8px',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                    border: '1px solid #E5E7EB',
                    marginBottom: '16px',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      padding: '16px',
                      cursor: 'pointer',
                      backgroundColor: expandedRows.has(message.id)
                        ? '#F9FAFB'
                        : '#FFFFFF',
                    }}
                    onClick={() => toggleRowExpansion(message.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleRowExpansion(message.id);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    aria-expanded={expandedRows.has(message.id)}
                    aria-label={`${
                      expandedRows.has(message.id) ? 'Collapse' : 'Expand'
                    } message details`}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'start',
                        marginBottom: '8px',
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            marginBottom: '4px',
                          }}
                        >
                          <p style={{ fontSize: '12px', color: '#6B7280' }}>
                            {message.date}
                          </p>
                          <span
                            style={{
                              fontSize: '12px',
                              color: '#6B7280',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                            }}
                          >
                            {message.channel === 'sms' ? (
                              <MessageSquare className="w-3 h-3" />
                            ) : (
                              <Bell className="w-3 h-3" />
                            )}
                            {message.channel === 'sms' ? 'SMS' : 'Push'}
                          </span>
                        </div>
                        <h3
                          style={{
                            fontSize: '16px',
                            fontWeight: 600,
                            color: '#1F2937',
                            marginBottom: '4px',
                          }}
                        >
                          {message.title}
                        </h3>
                        <p style={{ fontSize: '14px', color: '#6B7280' }}>
                          Sent by: {message.sentBy}
                        </p>
                      </div>
                      <ChevronRight
                        className={`w-5 h-5 transition-transform ${
                          expandedRows.has(message.id) ? 'rotate-90' : ''
                        }`}
                        style={{ color: '#6B7280', marginTop: '4px' }}
                      />
                    </div>

                    <div
                      style={{
                        display: 'flex',
                        gap: '16px',
                        marginTop: '12px',
                      }}
                    >
                      <div>
                        <span style={{ fontSize: '12px', color: '#6B7280' }}>
                          Revenue
                        </span>
                        <p
                          style={{
                            fontSize: '16px',
                            fontWeight: 600,
                            color: '#10B981',
                          }}
                        >
                          {message.revenue}
                        </p>
                      </div>
                      <div>
                        <span style={{ fontSize: '12px', color: '#6B7280' }}>
                          ROI
                        </span>
                        <p
                          style={{
                            fontSize: '16px',
                            fontWeight: 600,
                            color: '#10B981',
                          }}
                        >
                          {message.roi}
                        </p>
                      </div>
                    </div>
                  </div>

                  {expandedRows.has(message.id) && (
                    <div
                      style={{
                        padding: '16px',
                        backgroundColor: '#F9FAFB',
                        borderTop: '1px solid #E5E7EB',
                      }}
                    >
                      <div style={{ marginBottom: '16px' }}>
                        <h4
                          style={{
                            fontSize: '12px',
                            fontWeight: 600,
                            color: '#6B7280',
                            textTransform: 'uppercase',
                            marginBottom: '8px',
                          }}
                        >
                          Message
                        </h4>
                        <p
                          style={{
                            fontSize: '14px',
                            color: '#333333',
                            lineHeight: '1.6',
                          }}
                        >
                          {message.message}
                        </p>
                      </div>

                      <div
                        style={{
                          borderTop: '1px solid #E5E7EB',
                          paddingTop: '16px',
                        }}
                      >
                        <h4
                          style={{
                            fontSize: '12px',
                            fontWeight: 600,
                            color: '#6B7280',
                            textTransform: 'uppercase',
                            marginBottom: '12px',
                          }}
                        >
                          Performance
                        </h4>
                        <div
                          style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: '12px',
                          }}
                        >
                          <div>
                            <p style={{ fontSize: '12px', color: '#6B7280' }}>
                              Sent
                            </p>
                            <p
                              style={{
                                fontSize: '14px',
                                color: '#1F2937',
                                fontWeight: 600,
                              }}
                            >
                              {message.sent.toLocaleString()} recipients
                            </p>
                          </div>
                          <div>
                            <p style={{ fontSize: '12px', color: '#6B7280' }}>
                              Delivered
                            </p>
                            <p
                              style={{
                                fontSize: '14px',
                                color: '#1F2937',
                                fontWeight: 600,
                              }}
                            >
                              {message.delivered.toLocaleString()} (
                              {message.deliveryRate})
                            </p>
                          </div>
                          <div>
                            <p style={{ fontSize: '12px', color: '#6B7280' }}>
                              Clicked
                            </p>
                            <p
                              style={{
                                fontSize: '14px',
                                color: '#1F2937',
                                fontWeight: 600,
                              }}
                            >
                              {message.clicked} ({message.clickRate})
                            </p>
                          </div>
                          <div>
                            <p style={{ fontSize: '12px', color: '#6B7280' }}>
                              Cost
                            </p>
                            <p
                              style={{
                                fontSize: '14px',
                                color: '#1F2937',
                                fontWeight: 600,
                              }}
                            >
                              {message.cost}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Message Creation Modal */}
      {showMessageModal && selectedOpportunity && (
        <MessageCreationTool
          opportunity={selectedOpportunity}
          onSend={handleSendComplete}
          onClose={() => {
            setShowMessageModal(false);
            setSelectedOpportunity(null);
          }}
        />
      )}
    </div>
  );
};

export default MessageCenter;
